
import { Union, FSharpRef, Record } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { string_type, union_type, record_type, class_type, lambda_type, unit_type, obj_type, bool_type, float64_type, int32_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { getTimeMs } from "../Common/TimeHelpers.js";
import { fold, collect, append, empty, singleton, iterateIndexed, head, tail, ofArray, sumBy, map as map_1, exists, length, isEmpty, ofSeq, iterate } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { error, warn } from "../Shared/Log.js";
import { printf, toText, join, concat, interpolate } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { singleton as singleton_1, collect as collect_1, delay, toList, filter, map } from "../fable_modules/fable-library-js.5.13.0/Seq.js";
import { some, value as value_2 } from "../fable_modules/fable-library-js.5.13.0/Option.js";
import { disposeSafe, getEnumerator, equals, Exception, defaultOf } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { tryGetValue } from "../fable_modules/fable-library-js.5.13.0/MapUtil.js";
import { sidecarEndpoint } from "./Bridge.js";
import { dToInt } from "../Common/Ids.js";
import { fromZero, op_LeftShift, fromFloat64, op_Addition, op_RightShift, fromInt64, op_BitwiseAnd, toInt32 } from "../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { rangeDouble } from "../fable_modules/fable-library-js.5.13.0/Range.js";
import { RamView, RamRow, RamRowType } from "../Simulator/FastSim/RamView.js";
import { FSharpResult$2 } from "../fable_modules/fable-library-js.5.13.0/Result.js";
import { initialize } from "../fable_modules/fable-library-js.5.13.0/Array.js";
import { ComponentSlots, PortSlot } from "../Simulator/FastSim/PortView.js";

let socket = undefined;

class Pending extends Record {
    constructor(Cmd, SentAtMs, Warned, Resolve, Fail) {
        super();
        this.Cmd = (Cmd | 0);
        this.SentAtMs = SentAtMs;
        this.Warned = Warned;
        this.Resolve = Resolve;
        this.Fail = Fail;
    }
}

function Pending_$reflection() {
    return record_type("SidecarClient.Pending", [], Pending, () => [["Cmd", int32_type], ["SentAtMs", float64_type], ["Warned", bool_type], ["Resolve", lambda_type(obj_type, unit_type)], ["Fail", lambda_type(class_type("System.Exception"), unit_type)]]);
}

const pending = new Map([]);

let nextCorrId = 0;

function budgetMs(cmd) {
    let c_2;
    switch (cmd) {
        case 5:
            return undefined;
        case 7:
            return undefined;
        default:
            if ((c_2 = (cmd | 0), ((c_2 === 1) ? true : (c_2 === 2)) ? true : (c_2 === 3))) {
                return undefined;
            }
            else {
                return 2000;
            }
    }
}

function nameOf(cmd) {
    switch (cmd) {
        case 1:
            return "Echo";
        case 2:
            return "Upload";
        case 3:
            return "Download";
        case 4:
            return "SendDesign";
        case 5:
            return "SimBuild";
        case 6:
            return "SimRun";
        case 7:
            return "SimDigest";
        case 8:
            return "SimEnd";
        case 9:
            return "SimLog";
        case 10:
            return "SimSetInputs";
        case 11:
            return "SimRead";
        case 12:
            return "SimReadRam";
        case 13:
            return "SimPorts";
        case 14:
            return "SimReadDrivers";
        default:
            return `command ${cmd}`;
    }
}

function reportOverdue() {
    const now = getTimeMs();
    iterate((tupledArg) => {
        const entry = tupledArg[1];
        warn(`sidecar ${nameOf(entry.Cmd)} has not answered in ${interpolate("%.0f%P()", [now - entry.SentAtMs])}ms (invariant A6)`);
        pending.set(tupledArg[0], new Pending(entry.Cmd, entry.SentAtMs, true, entry.Resolve, entry.Fail));
    }, ofSeq(map((kv_1) => [kv_1[0], kv_1[1]], filter((kv) => {
        if (!kv[1].Warned) {
            const matchValue = budgetMs(kv[1].Cmd);
            if (matchValue == null) {
                return false;
            }
            else {
                return (now - kv[1].SentAtMs) > matchValue;
            }
        }
        else {
            return false;
        }
    }, pending))));
}

function dropSocket() {
    const option_1 = socket;
    if (option_1 != null) {
        const ws = value_2(option_1);
        ws.close();
    }
    socket = undefined;
}

const stuckMultiple = 10;

function failIfStuck(corrId, budget) {
    setTimeout(() => {
        let matchValue;
        let outArg = defaultOf();
        matchValue = [tryGetValue(pending, corrId, new FSharpRef(() => outArg, (v) => {
            outArg = v;
        })), outArg];
        if (matchValue[0]) {
            const entry = matchValue[1];
            pending.delete(corrId);
            error(`sidecar ${nameOf(entry.Cmd)} never answered in ${interpolate("%.0f%P()", [getTimeMs() - entry.SentAtMs])}ms; failing it and dropping the connection (invariant A4)`);
            entry.Fail(new Exception(concat("the sidecar did not answer ", nameOf(entry.Cmd))));
            dropSocket();
        }
    }, ~~(budget * stuckMultiple));
}

const startupBudgetMs = 60000;

function endpoint(deadline) {
    const matchValue = sidecarEndpoint();
    if (matchValue == null) {
        if (getTimeMs() > deadline) {
            return new Promise((_arg, reject) => {
                reject(new Exception(`the sidecar did not start listening within ${interpolate("%.0f%P()", [startupBudgetMs / 1000])}s`));
            });
        }
        else {
            const pr = new Promise((resolve, _arg_1) => {
                setTimeout(() => {
                    resolve();
                }, 100);
            });
            return pr.then(() => endpoint(deadline));
        }
    }
    else {
        const ep = matchValue;
        return Promise.resolve(ep);
    }
}

let connecting = undefined;

/**
 * Resolves once the socket is open, WAITING for the sidecar to be listening if it is still
 * starting. One socket per renderer: a second connect while one is open resolves immediately, and
 * one made while another is still connecting waits on that one.
 * 
 * Rejects only when the sidecar is genuinely not there - it never started, or it has died - which
 * is a fault and is reported as one. That distinction is the point of the wait above: everything
 * over this is written as though the sidecar is simply there, because by the time it is called it
 * is.
 */
export function connect() {
    const socket_1 = socket;
    const connecting_1 = connecting;
    if (socket_1 == null) {
        if (connecting_1 == null) {
            const attempt = new Promise((resolve, reject) => {
                let pr_1, pr;
                (pr_1 = ((pr = endpoint(getTimeMs() + startupBudgetMs), pr.then((tupledArg) => {
                    const ws = new WebSocket(`ws://127.0.0.1:${tupledArg[0]}/?token=${tupledArg[1]}`);
                    ws.binaryType = 'arraybuffer';
                    ws.onopen = ((_arg) => {
                        socket = some(ws);
                        connecting = undefined;
                        resolve();
                    });
                    ws.onerror = ((_arg_1) => {
                        socket = undefined;
                        connecting = undefined;
                        reject(new Exception("sidecar websocket failed"));
                    });
                    ws.onclose = ((_arg_2) => {
                        socket = undefined;
                        const dropped = ofSeq(pending.values());
                        pending.clear();
                        if (!isEmpty(dropped)) {
                            warn(`sidecar connection closed with ${length(dropped)} requests unanswered`);
                        }
                        iterate((entry) => {
                            entry.Fail(new Exception("the sidecar connection closed"));
                        }, dropped);
                    });
                    ws.onmessage = ((ev) => {
                        const frame = new Uint8Array(ev.data);
                        const corrId = new DataView(frame.buffer, frame.byteOffset).getUint32(1, true);
                        let matchValue_1;
                        let outArg = defaultOf();
                        matchValue_1 = [tryGetValue(pending, corrId, new FSharpRef(() => outArg, (v) => {
                            outArg = v;
                        })), outArg];
                        if (matchValue_1[0]) {
                            const entry_1 = matchValue_1[1];
                            pending.delete(corrId);
                            const took = getTimeMs() - entry_1.SentAtMs;
                            const matchValue_2 = budgetMs(entry_1.Cmd);
                            let matchResult, budget_1;
                            if (matchValue_2 != null) {
                                if ((took > matchValue_2) && !entry_1.Warned) {
                                    matchResult = 0;
                                    budget_1 = matchValue_2;
                                }
                                else {
                                    matchResult = 1;
                                }
                            }
                            else {
                                matchResult = 1;
                            }
                            switch (matchResult) {
                                case 0: {
                                    warn(`sidecar ${nameOf(entry_1.Cmd)} answered after ${interpolate("%.0f%P()", [took])}ms, past its ${interpolate("%.0f%P()", [budget_1])}ms budget (invariant A6)`);
                                    break;
                                }
                            }
                            reportOverdue();
                            entry_1.Resolve(frame);
                        }
                        else {
                            error(`sidecar: unmatched response, command ${frame[0]}`);
                        }
                    });
                }))), pr_1.catch((e) => {
                    connecting = undefined;
                    reject(e);
                }));
            });
            connecting = attempt;
            return attempt;
        }
        else {
            return connecting_1;
        }
    }
    else {
        return Promise.resolve(undefined);
    }
}

/**
 * One request, resolved with the whole response frame - header included, so a caller can size
 * what came back. The payload is a Uint8Array from makeBytes.
 * 
 * **Connects if there is no socket**, rather than refusing. A closed connection is a normal
 * event - the sidecar drops one when a handler faults, and the process behind it goes on holding
 * the session - so "connect first" as a permanent answer was wrong in the way that matters: only
 * a BUILD ever called `connect`, so after a drop every run and every read failed, the viewer
 * retried on its backoff for ever, and the simulation stayed dead until the user happened to
 * press Refresh. Reconnecting here costs nothing when a socket is open (one match) and is the
 * whole of the recovery when there is not.
 * 
 * It cannot storm: `connect` is single-flight, so concurrent callers join one attempt, and it
 * rejects rather than looping when the sidecar is genuinely gone.
 */
export function request(cmd, payload) {
    const sendOn = (ws) => (new Promise((resolve, reject) => {
        nextCorrId = (nextCorrId + 1);
        const corrId = nextCorrId;
        if (pending.has(corrId)) {
            error(`sidecar: correlation id ${corrId} is already in flight`);
        }
        const frame = new Uint8Array(8 + ~~(payload.length));
        frame[0] = cmd;
        new DataView(frame.buffer, frame.byteOffset).setUint32(1, corrId, true);
        frame.set(payload, 8);
        pending.set(corrId, new Pending(cmd, getTimeMs(), false, resolve, reject));
        const option_1 = budgetMs(cmd);
        if (option_1 != null) {
            failIfStuck(corrId, option_1);
        }
        reportOverdue();
        ws.send(frame);
    }));
    if (socket == null) {
        const pr = connect();
        return pr.then(() => {
            if (socket == null) {
                return new Promise((_arg, reject_1) => {
                    reject_1(new Exception("the sidecar connected without leaving a socket"));
                });
            }
            else {
                return sendOn(value_2(socket));
            }
        });
    }
    else {
        return sendOn(value_2(socket));
    }
}

/**
 * Whether a socket is open, and how many requests are waiting on the sidecar.
 * 
 * For the development harness, which is the only thing that asks: "is the sidecar there yet" is
 * otherwise unanswerable from outside, and the honest answer is what tells a test that has just
 * started the app to wait rather than to conclude the simulator is broken.
 */
export function connectionState() {
    return [socket != null, pending.size];
}

/**
 * Why a sidecar command did not answer with what was asked for.
 * 
 * **Two kinds, because only two things can be done about one.** A `Fault` is the sidecar
 * breaking - an exception escaped a handler, so an invariant the simulator maintains no longer
 * holds. That is a bug in Issie: it is logged as an error, which in a debug build puts it and its
 * stack on the screen. A `Refusal` is the sidecar declining something it anticipated and wrote a
 * message for - a stale epoch, a cycle it has not run to, a sheet that did not decode - which the
 * caller is expected to handle, and which is a warning unless the caller cannot.
 * 
 * **Anything unclassified is a Fault.** A failure nobody has said is expected is a bug until
 * somebody says otherwise; the alternative default quietly downgrades whatever is forgotten.
 * The strings are Protocol.FaultKind and Protocol.RefusalKind, mirrored here because the renderer
 * cannot see that file - the sidecar references the renderer, not the other way round.
 */
export class SidecarFailureKind extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Fault", "Refusal"];
    }
    static Fault = new SidecarFailureKind(0, []);
    static Refusal = new SidecarFailureKind(1, []);
}

export function SidecarFailureKind_$reflection() {
    return union_type("SidecarClient.SidecarFailureKind", [], SidecarFailureKind, () => [[], []]);
}

export class SidecarFailure extends Record {
    constructor(Kind, Message) {
        super();
        this.Kind = Kind;
        this.Message = Message;
    }
    /**
     * so that an existing `$"...{failure}"` says exactly what it always said
     */
    toString() {
        const this$ = this;
        return this$.Message;
    }
}

export function SidecarFailure_$reflection() {
    return record_type("SidecarClient.SidecarFailure", [], SidecarFailure, () => [["Kind", SidecarFailureKind_$reflection()], ["Message", string_type]]);
}

/**
 * The message inside an error payload, which is a JSON object whose only key is "error".
 * 
 * **Unwrapped here, so that no reader ever sees the envelope.** Readers used to take the payload
 * as it arrived, so a sidecar failure was logged as the literal `{"error":"..."}`. That was
 * merely untidy while the payload was one line; now that a stack trace travels in it, the
 * difference is between a readable trace and a run of escaped `\n`s.
 * 
 * A payload that is not the expected shape is returned as it came: this is the error path, and
 * something unreadable is better than nothing.
 */
export function failureOfPayload(payload) {
    if (payload.startsWith("{\"error\"")) {
        try {
            const parsed = JSON.parse(payload);
            const kind = (((function(o){ return o && typeof o.kind === 'string' ? o.kind : null })(parsed)) === "refusal") ? SidecarFailureKind.Refusal : SidecarFailureKind.Fault;
            const matchValue_1 = (function(o){ return o && typeof o.error === 'string' ? o.error : null })(parsed);
            return (matchValue_1 === defaultOf()) ? (new SidecarFailure(SidecarFailureKind.Fault, payload)) : (new SidecarFailure(kind, matchValue_1));
        }
        catch (matchValue_2) {
            return new SidecarFailure(SidecarFailureKind.Fault, payload);
        }
    }
    else {
        return new SidecarFailure(SidecarFailureKind.Fault, payload);
    }
}

/**
 * Just the message, for a caller that has already decided what the failure means.
 */
export function unwrapError(payload) {
    return failureOfPayload(payload).Message;
}

/**
 * Several failures as one, for an operation that made several requests and lost more than one of
 * them. **A fault among them makes the whole a fault**: the worst news decides what anyone has to
 * do about it, and a real bug must not be hidden by the refusals it arrived with.
 */
export function combineFailures(failures) {
    return new SidecarFailure(exists((f) => equals(f.Kind, SidecarFailureKind.Fault), failures) ? SidecarFailureKind.Fault : SidecarFailureKind.Refusal, join("; ", map_1((f_1) => f_1.Message, failures)));
}

/**
 * The same failure, said with the context the caller can add. **Keeps the kind**: putting
 * "reading the step panel: " in front of a fault does not make it a refusal, and the commonest
 * way to lose a classification is to rebuild the value as a string on the way past.
 */
export function prefixFailure(context, failure) {
    return new SidecarFailure(failure.Kind, concat(context, ": ", failure.Message));
}

/**
 * Report a failure at the severity its kind calls for, which is the whole reason the kind
 * exists. A fault is Issie broken and is an error - so in a debug build it is on the screen with
 * its stack a moment later. A refusal is the sidecar declining something a caller was supposed to
 * be ready for, so it is a warning: it belongs in the bug report and interrupts nobody.
 * 
 * The one call every reader of a sidecar failure should make, rather than choosing `Log.error`
 * for itself: choosing by hand is how the two came to mean the same thing.
 */
export function logFailure(context, failure) {
    const text = concat(context, ": ", failure.Message);
    if (failure.Kind.tag === 1) {
        warn(text);
    }
    else {
        error(text);
    }
}

/**
 * A failure nothing declined: the socket went, a request was never answered, or a reply broke the
 * contract it was supposed to keep. Not something any handler foresaw, so it is a fault - which
 * is also the default for anything unclassified, for the same reason.
 */
export function fault(message) {
    return new SidecarFailure(SidecarFailureKind.Fault, message);
}

/**
 * A transport failure: the socket went, or the request was never answered.
 */
export function transportFailure(message) {
    return fault(message);
}

export function errorOfFrame(frame) {
    if ((frame[0] & 0x40) !== 0) {
        return failureOfPayload(new TextDecoder().decode(frame.subarray(8)));
    }
    else {
        return undefined;
    }
}

function packStrings(strings) {
    const encoded = map_1((text) => (new TextEncoder().encode(text)), strings);
    const total = sumBy((bytes) => ((4 + ~~(bytes.length)) | 0), encoded, {
        GetZero: () => 0,
        Add: (x, y) => ((x + y) | 0),
    }) | 0;
    const payload = new Uint8Array(total);
    let offset = 0;
    const enumerator = getEnumerator(encoded);
    try {
        while (enumerator["System.Collections.IEnumerator.MoveNext"]()) {
            const bytes_1 = enumerator["System.Collections.Generic.IEnumerator`1.get_Current"]();
            new DataView(payload.buffer, payload.byteOffset).setUint32(offset, (bytes_1.length), true);
            payload.set(bytes_1, (offset + 4));
            offset = (((offset + 4) + ~~(bytes_1.length)) | 0);
        }
    }
    finally {
        disposeSafe(enumerator);
    }
    return payload;
}

/**
 * Send a design as ONE MESSAGE PER SHEET: which sheet of how many, then the top sheet's name
 * and that sheet's JSON.
 * 
 * Per sheet because decoding is the cost and it happens on the sidecar's serve loop, which
 * serves one message at a time - so a whole design in one message is one handler holding that
 * loop for ~300ms on 3cpu, against ~25ms for its largest single sheet. Per-sheet framing also
 * lets the sidecar reuse sheets it has already decoded, since an unchanged sheet serialises to
 * the identical string.
 * 
 * Sent in order, awaited one at a time. The sheets are a design only once the last has landed,
 * which is what the reply's `complete` says; sending them at once would arrive in any order and
 * give the sidecar no way to know when it had them all.
 * 
 * A design is only ever sent with every simulation closed - Start and Refresh both do it on a
 * closed one - so an upload never races a session. The sidecar drops whatever session it holds
 * when the first sheet arrives, so a command left over from before the design changed names an
 * epoch that no longer exists.
 * 
 * Resolves with the last reply, or stops at the first error and resolves with that.
 */
export function sendDesign(topSheet, sheetJsons) {
    const count = length(sheetJsons) | 0;
    const sendOne = (index, json) => {
        const strings = packStrings(ofArray([topSheet, json]));
        const payload = new Uint8Array(8 + ~~(strings.length));
        new DataView(payload.buffer, payload.byteOffset).setUint32(0, index, true);
        new DataView(payload.buffer, payload.byteOffset).setUint32(4, count, true);
        payload.set(strings, 8);
        const pr = request(4, payload);
        return pr.then((frame) => (new TextDecoder().decode(frame.subarray(8))));
    };
    const sendFrom = (index_1, remaining) => {
        if (!isEmpty(remaining)) {
            if (isEmpty(tail(remaining))) {
                return sendOne(index_1, head(remaining));
            }
            else {
                const pr_1 = sendOne(index_1, head(remaining));
                return pr_1.then((reply) => {
                    if (reply.startsWith("{\"error")) {
                        return Promise.resolve(reply);
                    }
                    else {
                        return sendFrom(index_1 + 1, tail(remaining));
                    }
                });
            }
        }
        else {
            return Promise.resolve(toText(printf("{\"error\":\"a design with no sheets\"}")));
        }
    };
    return sendFrom(0, sheetJsons);
}

function requestArgs(cmd, args) {
    const payload = new Uint8Array(4 * length(args));
    iterateIndexed((i, value) => {
        new DataView(payload.buffer, payload.byteOffset).setUint32((4 * i), value, true);
    }, args);
    const pr = request(cmd, payload);
    return pr.then((frame) => (new TextDecoder().decode(frame.subarray(8))));
}

/**
 * Build a simulation of the last-sent design's top sheet on the sidecar.
 */
export function simBuild(maxArraySize) {
    return requestArgs(5, singleton(maxArraySize));
}

/**
 * Advance the sidecar's simulation towards a cycle within a millisecond budget (0 = none);
 * the reply says where the clock got to. Chunk by repeating; cancel by stopping.
 * 
 * Every command that depends on a session names the session it means, and the sidecar refuses one
 * that names any other - see SimSession.checkEpoch. That is what stops a reply from a superseded
 * simulation being taken for a reply from this one.
 */
export function simRun(epoch, targetCycle, timeoutMs) {
    return requestArgs(6, ofArray([epoch, targetCycle, timeoutMs]));
}

/**
 * The sidecar's deterministic-stimulus digest text for the last-sent design (an error reply
 * starts with '{').
 */
export function simDigest(ticks) {
    return requestArgs(7, singleton(ticks));
}

export function simEnd(epoch) {
    return requestArgs(8, singleton(epoch));
}

/**
 * The sidecar's SimLog ring as JSON - the .NET half of a cross-runtime cost comparison.
 */
export function simLog() {
    return requestArgs(9, empty());
}

/**
 * Set top-level input values at a cycle: (component id, value) pairs, values up to 2^53
 * (split into low and high words on the wire). Reply is JSON.
 */
export function simSetInputs(epoch, cycle, values) {
    return requestArgs(10, append(ofArray([epoch, cycle, length(values)]), collect((tupledArg) => {
        const value = tupledArg[1];
        const hi = Math.floor(value / 4294967296);
        return ofArray([tupledArg[0], ~~(value - (hi * 4294967296)), ~~hi]);
    }, values)));
}

/**
 * THE waveform-data interface: for each signal - (component id, output port number, access
 * path root-first) - read `samples` values taken every `rep` cycles from `startCycle`. These
 * are the same (StartCycle, SamplingZoom, ShownCycles) parameters the waveform viewer's own
 * generation runs on, so a view at any zoom is one request. Resolves with the raw response
 * frame: on success the values are
 * `viewSimReadData frame (signals * samples * simReadWordsPerSample frame)`, signal-major and
 * zero-copy; an error response is one `errorOfFrame` answers for.
 * 
 * The word count is part of it and not a detail. A sample is as many uint32s as the widest signal
 * asked for needs, so a request carrying one wide bus widens every sample in that reply - and a
 * caller sizing the view as `signals * samples` reads a fraction of what was sent, silently.
 */
export function simRead(epoch, startCycle, rep, samples, signals) {
    const args = append(ofArray([epoch, startCycle, rep, samples, length(signals)]), collect((tupledArg) => {
        const path = tupledArg[2];
        return append(ofArray([tupledArg[0], tupledArg[1], length(path)]), path);
    }, signals));
    const payload = new Uint8Array(4 * length(args));
    iterateIndexed((i, value) => {
        new DataView(payload.buffer, payload.byteOffset).setUint32((4 * i), value, true);
    }, args);
    return request(11, payload);
}

/**
 * SimRead by driver handle: the indices the port slice handed out, valid for this build.
 * Resolves with the raw response frame, exactly as `simRead` does - same reply layout, same
 * zero-copy view - so the two are interchangeable to everything downstream.
 */
export function simReadDrivers(epoch, startCycle, rep, samples, drivers) {
    const args = append(ofArray([epoch, startCycle, rep, samples, length(drivers)]), map_1((arg00$0040) => (dToInt(arg00$0040) | 0), drivers));
    const payload = new Uint8Array(4 * length(args));
    iterateIndexed((i, value) => {
        new DataView(payload.buffer, payload.byteOffset).setUint32((4 * i), value, true);
    }, args);
    return request(14, payload);
}

/**
 * One memory's contents at one clock, as a RAM table shows them.
 * 
 * `sparseUpTo` is the most non-zero locations worth listing; past that a window of `rows` from
 * `start` comes back instead, and zero asks for a window whatever the memory holds. Which of the
 * two arrives is the sidecar's decision - only it knows how much the memory holds - so the reply
 * says which it is and the caller draws accordingly.
 */
export function simReadRam(epoch, cycle, compId, path, sparseUpTo, start, rows) {
    const args = append(ofArray([epoch, cycle, compId, length(path)]), append(path, ofArray([sparseUpTo, ~~toInt32(op_BitwiseAnd(start, fromInt64(4294967295n))), ~~toInt32(op_BitwiseAnd(op_RightShift(start, 32), fromInt64(4294967295n))), rows])));
    const payload = new Uint8Array(4 * length(args));
    iterateIndexed((i, value) => {
        new DataView(payload.buffer, payload.byteOffset).setUint32((4 * i), value, true);
    }, args);
    const pr = request(12, payload);
    return pr.then((frame) => {
        const matchValue = errorOfFrame(frame);
        if (matchValue == null) {
            const isSparse = (new DataView(frame.buffer, frame.byteOffset).getUint32(8, true)) === 1;
            const rowCount = ~~(new DataView(frame.buffer, frame.byteOffset).getUint32(12, true)) | 0;
            const wordsPerValue = ~~(new DataView(frame.buffer, frame.byteOffset).getUint32(16, true)) | 0;
            const allRows = toList(delay(() => map((i_3) => {
                let matchValue_1;
                const at = (24 + ((12 + (4 * wordsPerValue)) * i_3)) | 0;
                return new RamRow(op_Addition(fromFloat64(new DataView(frame.buffer, frame.byteOffset).getUint32(at, true)), op_LeftShift(fromFloat64(new DataView(frame.buffer, frame.byteOffset).getUint32((at + 4), true)), 32)), fold((acc, w) => op_Addition(op_LeftShift(acc, 32), fromFloat64(new DataView(frame.buffer, frame.byteOffset).getUint32(((at + 12) + (4 * w)), true))), fromZero(), toList(rangeDouble(wordsPerValue - 1, -1, 0))), (matchValue_1 = (~~(new DataView(frame.buffer, frame.byteOffset).getUint32((at + 8), true)) | 0), (matchValue_1 === 1) ? RamRowType.RAMRead : ((matchValue_1 === 2) ? RamRowType.RAMWritten : RamRowType.RAMNormal)));
            }, rangeDouble(0, 1, rowCount - 1))));
            return new FSharpResult$2(/* Ok */ 0, [isSparse ? (new RamView(/* RamSparse */ 0, [allRows])) : (new RamView(/* RamWindow */ 1, [start, allRows]))]);
        }
        else {
            return new FSharpResult$2(/* Error */ 1, [matchValue]);
        }
    });
}

/**
 * Width and driver index of every port of every component on one instance's sheet - the wave
 * selector's read, made when its combo boxes pick an instance. Decoded into the SAME type the
 * renderer's own simulator answers with (PortView.sheetSliceOf), so everything downstream is
 * one code path and only the source of the bytes differs.
 */
export function simPorts(epoch, path) {
    const args = append(ofArray([epoch, length(path)]), path);
    const payload = new Uint8Array(4 * length(args));
    iterateIndexed((i, value) => {
        new DataView(payload.buffer, payload.byteOffset).setUint32((4 * i), value, true);
    }, args);
    const pr = request(13, payload);
    return pr.then((frame) => {
        const matchValue = errorOfFrame(frame);
        if (matchValue == null) {
            const compCount = ~~(new DataView(frame.buffer, frame.byteOffset).getUint32(8, true)) | 0;
            let at = 12;
            const readU32 = () => {
                const v = ~~(new DataView(frame.buffer, frame.byteOffset).getUint32(at, true)) | 0;
                at = ((at + 4) | 0);
                return v | 0;
            };
            const readSlots = (n) => initialize(n, (_arg) => (new PortSlot(readU32(), readU32())));
            return new FSharpResult$2(/* Ok */ 0, [toList(delay(() => collect_1((matchValue_1) => {
                let cid, nIns, nOuts;
                return singleton_1((cid = (readU32() | 0), (nIns = (readU32() | 0), (nOuts = (readU32() | 0), new ComponentSlots(cid, readSlots(nIns), readSlots(nOuts))))));
            }, rangeDouble(1, 1, compCount))))]);
        }
        else {
            return new FSharpResult$2(/* Error */ 1, [matchValue]);
        }
    });
}

